import { useState, useEffect, useCallback } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Input } from '@/components/ui/input'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Checkbox } from '@/components/ui/checkbox'
import { DatePickerWithRange } from '@/components/ui/date-range-picker'
import type { DateRange } from 'react-day-picker'
import { Button } from '@/components/ui/button'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Contact, Filter, RotateCcw, AlertTriangle, Lock } from 'lucide-react'
import { ContactMessageTable } from '@/components/mailbox/ContactMessageTable'
import { ContactMessageDetailView } from '@/components/mailbox/ContactMessageDetailView'
import { useProgramContext } from '@/context/ProgramContext'
import { addDays } from 'date-fns'
import { motion } from 'framer-motion'
import { apiClient } from '@/lib/api'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
	Pagination,
	PaginationContent,
	PaginationItem,
	PaginationLink,
	PaginationNext,
	PaginationPrevious,
	PaginationEllipsis,
} from '@/components/ui/pagination'
import { cn } from '@/lib/utils'
import { useTranslation } from '@/hooks/useTranslation'

interface ContactMessage {
	id: string
	description: string
	status: string
	severity: string
	created_at: string
	updated_at: string
	screenshot_url: string | null
	user_metadata: any // Define more strictly if possible
	reporter_email: string | null
	reporter_name: string | null
	assignee_user_id: string | null
	widget_id: string
	program_id: string
	widget?: { name: string }
	program?: { name: string }
}

interface ContactMessageApiResponse {
	contacts: ContactMessage[]
	totalMatchingMailbox: number
	totalLifetimeMailbox: number
	viewableLimit: number
	hiddenCount: number
	isPro: boolean
	hasMore: boolean
	currentPage: number
	itemsPerPage: number
	totalPages: number
}

const getSeverityOptions = (t: (key: string) => string) => [
	{ label: t('contactMessages.allSeverities'), value: 'all' },
	{ label: t('contactMessages.critical'), value: 'critical' },
	{ label: t('contactMessages.high'), value: 'high' },
	{ label: t('contactMessages.medium'), value: 'medium' },
	{ label: t('contactMessages.low'), value: 'low' },
]

const getStatusOptions = (t: (key: string) => string) => [
	{ label: t('contactMessages.statusNew'), value: 'new' },
	{ label: t('contactMessages.statusInProgress'), value: 'in_progress' },
	{ label: t('contactMessages.statusResolved'), value: 'resolved' },
	{ label: t('contactMessages.statusClosed'), value: 'closed' },
	{ label: t('contactMessages.statusArchived'), value: 'archived' },
]

const getSortOptions = (t: (key: string) => string) => [
	{ label: t('contactMessages.sortOldestFirst'), value: 'created_at_asc' },
	{ label: t('contactMessages.sortLastUpdated'), value: 'updated_at_desc' },
	{ label: t('contactMessages.sortSeverityHighToLow'), value: 'severity_desc' },
	{ label: t('contactMessages.sortStatus'), value: 'status_asc' },
]

export default function Mailbox() {
	const { t } = useTranslation()
	const severityOptions = getSeverityOptions(t)
	const statusOptions = getStatusOptions(t)
	const sortOptions = getSortOptions(t)
	const {
		currentProgram,
		programId,
		loading: programLoading,
	} = useProgramContext()
	const [search, setSearch] = useState('')
	const [selectedSeverities, setSelectedSeverities] = useState<string[]>([
		'critical',
		'high',
		'medium',
		'low',
	])
	const [selectedStatuses, setSelectedStatuses] = useState<string[]>([
		'new',
		'in_progress',
	])
	const [sort, setSort] = useState('updated_at_desc')
	const [dateRange, setDateRange] = useState<DateRange | undefined>({
		from: new Date('2025-01-01'),
		to: new Date(),
	})
	const [selectedContactId, setSelectedContactId] = useState<string | null>(null)
	const [searchParams, setSearchParams] = useSearchParams()
	const navigate = useNavigate()

	const [currentPage, setCurrentPage] = useState(
		parseInt(searchParams.get('page') || '1', 10)
	)
	const PER_PAGE_OPTIONS = [15, 50, 100] as const
	const [itemsPerPage, setItemsPerPage] = useState<number>(15)

	useEffect(() => {
		const contactIdFromUrl = searchParams.get('contactId')
		if (contactIdFromUrl && contactIdFromUrl !== selectedContactId) {
			setSelectedContactId(contactIdFromUrl)
		} else if (!contactIdFromUrl && selectedContactId) {
			setSelectedContactId(null)
		}
	}, [searchParams, selectedContactId])

	const {
		data: apiResponse,
		isLoading,
		error,
		refetch,
	} = useQuery<ContactMessageApiResponse, Error>({
		queryKey: [
			'contacts',
			programId,
			{
				search,
				selectedSeverities,
				selectedStatuses,
				sort,
				dateRange,
				currentPage,
				itemsPerPage,
			},
		],
		queryFn: async () => {
			if (!programId)
				return {
					contacts: [],
					totalMatchingMailbox: 0,
					totalLifetimeMailbox: 0,
					viewableLimit: 0,
					hiddenCount: 0,
					isPro: false,
					hasMore: false,
					currentPage: 1,
					itemsPerPage,
					totalPages: 0,
				}

			const params = new URLSearchParams({
				programId,
				limit: String(itemsPerPage),
				page: String(currentPage),
			})
			if (search) params.append('search', search)
			if (selectedSeverities.length > 0)
				params.append('severities', selectedSeverities.join(','))
			if (selectedStatuses.length > 0)
				params.append('statuses', selectedStatuses.join(','))

			if (sort) params.append('sort', sort)
			if (dateRange?.from)
				params.append('fromDate', dateRange.from.toISOString())
			if (dateRange?.to)
				params.append(
					'toDate',
					new Date(dateRange.to.setHours(23, 59, 59, 999)).toISOString()
				)

			return apiClient<ContactMessageApiResponse>(
				`/api/contacts/protected?${params.toString()}`
			)
		},
		enabled: !!programId && !programLoading,
		placeholderData: keepPreviousData,
	})

	useEffect(() => {
		// Reset to page 1 when filters change, but not on initial load or when programId is not set yet
		const prevProgramId = localStorage.getItem('prevProgramIdForContactFilter')
		if (programId && prevProgramId !== programId) {
			setCurrentPage(1)
			setSearchParams((prev) => ({ ...prev, page: '1' }), { replace: true })
			localStorage.setItem('prevProgramIdForContactFilter', programId)
		} else if (!programId) {
			localStorage.removeItem('prevProgramIdForContactFilter')
		}
	}, [programId, setSearchParams])

	useEffect(() => {
		if (currentPage !== 1) {
			setCurrentPage(1)
			setSearchParams((prev) => ({ ...prev, page: '1' }), { replace: true })
		}
	}, [dateRange, selectedSeverities, selectedStatuses, search])

	useEffect(() => {
		if (currentPage !== 1) {
			setCurrentPage(1)
			setSearchParams((prev) => ({ ...prev, page: '1' }), { replace: true })
		}
	}, [itemsPerPage])

	useEffect(() => {
		const pageFromUrl = parseInt(searchParams.get('page') || '1', 10)
		setCurrentPage(pageFromUrl)
	}, [searchParams])

	const handlePageChange = useCallback((newPage: number) => {
		setCurrentPage(newPage)
		setSearchParams(
			(prev) => {
				const newSearchQuery = new URLSearchParams(prev)
				newSearchQuery.set('page', String(newPage))
				return newSearchQuery
			},
			{ replace: true }
		)
	}, [setSearchParams])

	const handleReset = () => {
		setSearch('')
		setSelectedSeverities(['critical', 'high', 'medium', 'low'])
		setSelectedStatuses(['new', 'in_progress'])
		setSort('updated_at_desc')
		setDateRange({ from: new Date('2025-01-01'), to: new Date() })
		const newParams = new URLSearchParams()
		if (searchParams.has('contactId'))
			newParams.set('contactId', searchParams.get('contactId')!)
		newParams.set('page', '1')
		setSearchParams(newParams, { replace: true })
		setCurrentPage(1)
	}

	const toggleStatus = (status: string) => {
		setSelectedStatuses((prev) => {
			const newStatuses = prev.includes(status)
				? prev.filter((s) => s !== status)
				: [...prev, status]
			return newStatuses
		})
	}

	const toggleSeverity = (severity: string) => {
		setSelectedSeverities((prev) => {
			const newSeverities = prev.includes(severity)
				? prev.filter((s) => s !== severity)
				: [...prev, severity]
			return newSeverities
		})
	}

	if (programLoading) return <Skeleton className="h-screen w-full" />

	if (error) {
		return (
			<div className="container py-6 text-center">
				<AlertTriangle className="mx-auto h-12 w-12 text-destructive" />
				<h2 className="mt-4 text-xl font-semibold">
					{t('contactMessages.errorLoadingMailbox')}
				</h2>
				<p className="mt-2 text-muted-foreground">{error.message}</p>
				<Button onClick={() => refetch()} className="mt-4">
					{t('contactMessages.retry')}
				</Button>
			</div>
		)
	}

	const rawContacts = apiResponse?.contacts || []
	const contactsToDisplay =
		selectedStatuses.length === 0 || selectedSeverities.length === 0
			? []
			: rawContacts
	const totalPages = apiResponse?.totalPages || 1

	return (
		<div className="container py-6 space-y-6">
			<motion.div
				initial={{ opacity: 0, y: -20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3 }}
				className="bg-gradient-to-r from-slate-50 to-white dark:from-slate-900 dark:to-slate-800 rounded-xl p-6 shadow-sm border border-border/50">
				<div className="flex items-center gap-3">
					<div className="p-2 bg-primary/10 rounded-lg">
						<Contact className="h-5 w-5 text-primary" />
					</div>
					<div>
						<h1 className="text-xl md:text-2xl font-semibold text-foreground">
							{t('contactMessages.contactMessages')}
						</h1>
						<p className="text-sm text-muted-foreground mt-0.5">
							{t('contactMessages.viewAndManageIssues', { programName: currentProgram?.name || t('contactMessages.yourProgram') })}
						</p>
					</div>
				</div>
			</motion.div>

			<Card className="p-4">
				<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5">
					<Input
						placeholder={t('contactMessages.searchContacts')}
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="xl:col-span-1"
					/>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="outline" className="w-full justify-between">
								{t('contactMessages.severity')} ({selectedSeverities.length}/
								{severityOptions.filter((opt) => opt.value !== 'all').length})
								<span className="sr-only">{t('contactMessages.toggleSeverityMenu')}</span>
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent className="w-56 p-3">
							<div className="space-y-3">
								<div className="flex gap-2">
									<Button
										variant="outline"
										size="sm"
										className="flex-1 text-xs"
										onClick={() => {
											const allSeverities = severityOptions.filter(
												(opt) => opt.value !== 'all'
											).map((opt) => opt.value)
											setSelectedSeverities(allSeverities)
										}}>
										{t('contactMessages.selectAll')}
									</Button>
									<Button
										variant="outline"
										size="sm"
										className="flex-1 text-xs"
										onClick={() => {
											setSelectedSeverities([])
										}}>
										{t('contactMessages.unselectAll')}
									</Button>
								</div>
								<div className="space-y-2">
									{severityOptions.filter(
										(option) => option.value !== 'all'
									).map((option) => (
										<div
											key={option.value}
											className="flex items-center space-x-2">
											<Checkbox
												id={`severity-${option.value}`}
												checked={selectedSeverities.includes(option.value)}
												onCheckedChange={() => toggleSeverity(option.value)}
											/>
											<label
												htmlFor={`severity-${option.value}`}
												className="text-sm font-medium cursor-pointer flex-1">
												{option.label}
											</label>
										</div>
									))}
								</div>
							</div>
						</DropdownMenuContent>
					</DropdownMenu>
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="outline" className="w-full justify-between">
								{t('contactMessages.status')} ({selectedStatuses.length}/{statusOptions.length})
								<span className="sr-only">{t('contactMessages.toggleStatusMenu')}</span>
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent className="w-56 p-3">
							<div className="space-y-3">
								<div className="flex gap-2">
									<Button
										variant="outline"
										size="sm"
										className="flex-1 text-xs"
										onClick={() => {
											const allStatuses = statusOptions.map((opt) => opt.value)
											setSelectedStatuses(allStatuses)
										}}>
										{t('contactMessages.selectAll')}
									</Button>
									<Button
										variant="outline"
										size="sm"
										className="flex-1 text-xs"
										onClick={() => {
											setSelectedStatuses([])
										}}>
										{t('contactMessages.unselectAll')}
									</Button>
								</div>
								<div className="space-y-2">
									{statusOptions.map((option) => (
										<div
											key={option.value}
											className="flex items-center space-x-2">
											<Checkbox
												id={`status-${option.value}`}
												checked={selectedStatuses.includes(option.value)}
												onCheckedChange={() => toggleStatus(option.value)}
											/>
											<label
												htmlFor={`status-${option.value}`}
												className="text-sm font-medium cursor-pointer flex-1">
												{option.label}
											</label>
										</div>
									))}
								</div>
							</div>
						</DropdownMenuContent>
					</DropdownMenu>

					<div className="md:col-span-2 lg:col-span-4 xl:col-span-1">
						<DatePickerWithRange
							date={dateRange}
							setDate={(newRange) => setDateRange(newRange)}
						/>
					</div>
					<div className="xl:col-start-5 flex justify-end items-center">
						<Button variant="outline" onClick={handleReset} size="sm">
							<RotateCcw className="mr-2 h-4 w-4" /> {t('contactMessages.reset')}
						</Button>
					</div>
				</div>
			</Card>

			{apiResponse && !apiResponse.isPro && apiResponse.hiddenCount > 0 && (
				<Alert
					variant="default"
					className="mb-6 bg-amber-50 border-amber-300 dark:bg-amber-900/30 dark:border-amber-700">
					<Lock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
					<AlertTitle className="font-semibold text-amber-700 dark:text-amber-300">
						{t('contactMessages.viewAllMailbox')}
					</AlertTitle>
					<AlertDescription className="text-amber-600 dark:text-amber-400">
						{t('contactMessages.collectedMailbox', {
							total: apiResponse.totalLifetimeMailbox,
							limit: apiResponse.viewableLimit,
							hidden: apiResponse.hiddenCount,
						})}
						<Button
							variant="link"
							className="p-0 h-auto text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200 underline ml-1 font-medium"
							onClick={() => navigate('/settings?tab=billing')}>
							{t('contactMessages.upgradeToProToSeeAll')}
						</Button>
					</AlertDescription>
				</Alert>
			)}

			{isLoading && !apiResponse ? (
				<ContactMessageTable
					contacts={[]}
					isLoading={true}
					sort={sort}
					onSortChange={setSort}
				/>
			) : contactsToDisplay.length > 0 ||
			  (!apiResponse?.isPro &&
					apiResponse?.hiddenCount &&
					apiResponse.hiddenCount > 0 &&
					contactsToDisplay.length < itemsPerPage) ? (
				<ContactMessageTable
					contacts={contactsToDisplay}
					isLoading={isLoading && !!apiResponse}
					onContactSelect={(contactId) => {
						setSelectedContactId(contactId)
						setSearchParams(
							(prev) => {
								const newSearchQuery = new URLSearchParams(prev)
								if (contactId) {
									newSearchQuery.set('contactId', contactId)
								} else {
									newSearchQuery.delete('contactId')
								}
								return newSearchQuery
							},
							{ replace: true }
						)
					}}
					isPro={apiResponse?.isPro}
					hiddenCount={apiResponse?.hiddenCount || 0}
					itemsPerPage={itemsPerPage}
					sort={sort}
					onSortChange={setSort}
				/>
			) : (
				<Card>
					<CardContent className="text-center py-12 text-muted-foreground">
						{t('contactMessages.noMailboxFound')}
					</CardContent>
				</Card>
			)}

			{apiResponse &&
				selectedStatuses.length > 0 &&
				selectedSeverities.length > 0 &&
				(totalPages > 1 || contactsToDisplay.length > 0) && (
				<div className="flex flex-wrap items-center justify-between gap-4">
					<div className="flex items-center gap-2">
						<span className="text-sm text-muted-foreground whitespace-nowrap">
							{t('contactMessages.perPage')}
						</span>
						<Select
							value={String(itemsPerPage)}
							onValueChange={(v) => setItemsPerPage(Number(v))}>
							<SelectTrigger className="w-[72px]">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{PER_PAGE_OPTIONS.map((n) => (
									<SelectItem key={n} value={String(n)}>
										{n}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					{totalPages > 1 && (
				<Pagination>
					<PaginationContent>
						<PaginationItem>
							<PaginationPrevious
								onClick={() =>
									currentPage > 1 &&
									!isLoading &&
									handlePageChange(currentPage - 1)
								}
								aria-disabled={currentPage === 1 || isLoading}
								className={cn(
									(currentPage === 1 || isLoading) &&
										'pointer-events-none opacity-50'
								)}
								href="#"
							/>
						</PaginationItem>

						{Array.from({ length: totalPages }, (_, i) => i + 1).map(
							(pageNum) => {
								const showPageLink =
									pageNum === 1 ||
									pageNum === totalPages ||
									(pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
								const showEllipsisStart =
									pageNum === currentPage - 2 &&
									currentPage > 3 &&
									totalPages > 5
								const showEllipsisEnd =
									pageNum === currentPage + 2 &&
									currentPage < totalPages - 2 &&
									totalPages > 5

								if (showPageLink) {
									return (
										<PaginationItem key={pageNum}>
											<PaginationLink
												onClick={() => !isLoading && handlePageChange(pageNum)}
												isActive={currentPage === pageNum}
												className={cn(
													isLoading && 'pointer-events-none opacity-50'
												)}
												href="#">
												{pageNum}
											</PaginationLink>
										</PaginationItem>
									)
								}
								if (showEllipsisStart || showEllipsisEnd) {
									return (
										<PaginationItem key={`ellipsis-${pageNum}`}>
											<PaginationEllipsis />
										</PaginationItem>
									)
								}
								return null
							}
						)}

						<PaginationItem>
							<PaginationNext
								onClick={() =>
									currentPage < totalPages &&
									!isLoading &&
									handlePageChange(currentPage + 1)
								}
								aria-disabled={currentPage === totalPages || isLoading}
								className={cn(
									(currentPage === totalPages || isLoading) &&
										'pointer-events-none opacity-50'
								)}
								href="#"
							/>
						</PaginationItem>
					</PaginationContent>
				</Pagination>
					)}
				</div>
			)}

			{selectedContactId && (
				<ContactMessageDetailView
					contactId={selectedContactId}
					onClose={() => {
						if (selectedContactId) {
							setSelectedContactId(null)
							setSearchParams(
								(prev) => {
									const newSearchQuery = new URLSearchParams(prev)
									newSearchQuery.delete('contactId')
									return newSearchQuery
								},
								{ replace: true }
							)
						}
					}}
				/>
			)}
		</div>
	)
}
